import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { CampaignDetail, MailboxRow } from '../outreachTypes';
import { shortDate } from '../outreachFormat';
import { ANGLE_COPY } from './StepMessage';
import type { BuilderStep } from './builderSteps';
import { campaignMessages } from './campaignMessages';
import { startMessages as messages } from './startMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the setup; each part uses its own slice of it */
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RecapProps {
  campaign: CampaignDetail;
  /** The people that fit now (the kinds of the campaign). */
  fitNow: number;
  blocks: number;
  /** The mailboxes of the campaign that can send. */
  from: MailboxRow[];
  /** The cap of the warm-up today, when it is below the daily rate; else null. */
  warmupCap: number | null;
  onEdit: (step: BuilderStep) => void;
}

/** The six recap cells of step 5 (design outreach-v2 f1 `recap`), each with "Edit". */
const RecapComponent: React.FC<RecapProps> = ({ campaign, fitNow, blocks, from, warmupCap, onEdit }) => {
  const intl = useIntl();
  const cells = useMemo(() => {
    const none = intl.formatMessage(messages.recapNone);
    const angle: MessageDescriptor = ANGLE_COPY[campaign.angle ?? 'auto']?.title ?? campaignMessages.angleAuto;
    const items: Array<{ key: string; label: MessageDescriptor; value: string; step: BuilderStep }> = [
      {
        key: 'song',
        label: messages.recapSong,
        value: intl.formatMessage(messages.recapSongValue, {
          song: campaign.songTitle,
          date: shortDate(intl, campaign.releaseDate),
        }),
        step: 'song',
      },
      {
        key: 'who',
        label: messages.recapWho,
        value: intl.formatMessage(messages.recapWhoValue, {
          kinds: campaign.kinds.length,
          fit: intl.formatNumber(fitNow),
        }),
        step: 'who',
      },
      {
        key: 'perDay',
        label: messages.recapPerDay,
        value:
          warmupCap === null
            ? String(campaign.dailyRate)
            : intl.formatMessage(messages.recapPerDayWarmup, { rate: campaign.dailyRate, cap: warmupCap }),
        step: 'who',
      },
      {
        key: 'message',
        label: messages.recapMessage,
        value:
          campaign.angle === null ? intl.formatMessage(campaignMessages.navMessageAuto) : intl.formatMessage(angle),
        step: 'message',
      },
      {
        key: 'from',
        label: messages.recapFrom,
        value: from.length === 0 ? none : from.map((mailbox) => mailbox.address).join(', '),
        step: 'sendFrom',
      },
      {
        key: 'never',
        label: messages.recapNever,
        value: intl.formatMessage(messages.recapNeverValue, { count: blocks }),
        step: 'who',
      },
    ];
    return items.map((item) => (
      <div key={item.key} className={styles.recapCell} data-testid={`recap-${item.key}`}>
        <div className={styles.recapKey}>
          {intl.formatMessage(item.label)}
          <button type="button" className={styles.recapEdit} onClick={() => onEdit(item.step)}>
            {intl.formatMessage(messages.recapEdit)}
          </button>
        </div>
        <div className={styles.recapValue}>{item.value}</div>
      </div>
    ));
  }, [blocks, campaign, fitNow, from, intl, onEdit, warmupCap]);
  return <div className={styles.recap}>{cells}</div>;
};

export const Recap = TypedMemo(RecapComponent);
